import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const DecimalDragons = () => {
  const navigate = useNavigate();
  const [score, setScore] = useState(0);
  const [question, setQuestion] = useState(null);
  const [dragonHealth, setDragonHealth] = useState(100);
  const [playerHealth, setPlayerHealth] = useState(100);
  const [userAnswer, setUserAnswer] = useState('');
  const [feedback, setFeedback] = useState('');
  const [dragonsDefeated, setDragonsDefeated] = useState(0);
  const [isAttacking, setIsAttacking] = useState(false);
  const [dragonAttacking, setDragonAttacking] = useState(false);
  const [level, setLevel] = useState(1);

  const operations = [
    { a: 0.5, b: 0.3, op: '+', answer: 0.8 },
    { a: 1.2, b: 0.7, op: '+', answer: 1.9 },
    { a: 2.5, b: 1.3, op: '+', answer: 3.8 },
    { a: 0.9, b: 0.4, op: '-', answer: 0.5 },
    { a: 1.5, b: 0.8, op: '-', answer: 0.7 },
    { a: 3.2, b: 1.5, op: '-', answer: 1.7 },
    { a: 4.6, b: 2.3, op: '+', answer: 6.9 },
    { a: 5.5, b: 3.2, op: '-', answer: 2.3 },
    { a: 0.6, b: 0.9, op: '+', answer: 1.5 },
    { a: 2.8, b: 1.4, op: '-', answer: 1.4 },
  ];

  const generateQuestion = () => {
    const q = operations[Math.floor(Math.random() * operations.length)];
    setQuestion(q);
    setUserAnswer('');
    setFeedback('');
    setDragonHealth(100);
  };

  useEffect(() => {
    generateQuestion();
  }, []);

  useEffect(() => {
    // Dragon attacks periodically
    if (dragonHealth > 0 && playerHealth > 0) {
      const attackInterval = setInterval(() => {
        setDragonAttacking(true);
        setTimeout(() => {
          setPlayerHealth(h => Math.max(0, h - 10));
          setDragonAttacking(false);
        }, 500);
      }, 5000);

      return () => clearInterval(attackInterval);
    } else if (playerHealth === 0) {
      // Game over
    }
  }, [dragonHealth, playerHealth]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!question) return;

    const answer = parseFloat(userAnswer);
    if (Math.abs(answer - question.answer) < 0.01) {
      setIsAttacking(true);
      const damage = 25;
      setDragonHealth(h => Math.max(0, h - damage));
      setFeedback(`💥 Hit! -${damage} HP`);
      
      setTimeout(() => {
        setIsAttacking(false);
        if (dragonHealth - damage <= 0) {
          setScore(score + (20 * level));
          setDragonsDefeated(dragonsDefeated + 1);
          setFeedback('🎉 Dragon Defeated!');
          setLevel(l => l + 1);
          setTimeout(() => {
            generateQuestion();
            setPlayerHealth(h => Math.min(100, h + 20));
          }, 1500);
        } else {
          setFeedback('');
          setUserAnswer('');
        }
      }, 500);
    } else {
      setFeedback(`❌ Miss! Answer: ${question.answer}`);
      setPlayerHealth(h => Math.max(0, h - 5));
      setTimeout(() => {
        setFeedback('');
        setUserAnswer('');
      }, 1500);
    }
  };

  if (playerHealth === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 via-orange-800 to-yellow-700 py-8 px-4 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-2xl p-8 text-center max-w-md">
          <div className="text-6xl mb-4">💀</div>
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Defeated!</h2>
          <div className="text-xl text-gray-600 mb-6">
            <p>Dragons Defeated: <span className="font-bold text-red-600">{dragonsDefeated}</span></p>
            <p>Final Score: <span className="font-bold text-red-600">{score}</span></p>
            <p>Level Reached: <span className="font-bold text-red-600">{level}</span></p>
          </div>
          <div className="space-x-4">
            <button
              onClick={() => window.location.reload()}
              className="bg-red-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-red-700"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/games')}
              className="bg-gray-600 text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-700"
            >
              Back to Games
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-900 via-orange-800 to-yellow-700 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 mb-4 text-white">
          <div className="flex justify-between items-center">
            <button onClick={() => navigate('/games')} className="hover:text-yellow-300">
              ← Back
            </button>
            <div className="text-center">
              <h1 className="text-3xl font-bold">🐉 Decimal Dragons</h1>
              <div className="text-sm">Level {level} | Dragons Defeated: {dragonsDefeated}</div>
            </div>
            <div className="text-2xl font-bold">{score} pts</div>
          </div>
        </div>

        {/* Battle Arena */}
        <div className="bg-black/30 rounded-xl p-6 mb-4" style={{ minHeight: '400px' }}>
          <div className="grid grid-cols-2 gap-8 h-full">
            {/* Dragon Side */}
            <div className="text-center">
              <div className="mb-4">
                <div className="text-sm text-white mb-2">Dragon HP</div>
                <div className="w-full bg-gray-700 rounded-full h-4 overflow-hidden">
                  <div 
                    className="bg-gradient-to-r from-red-500 to-orange-500 h-full transition-all duration-300"
                    style={{ width: `${dragonHealth}%` }}
                  />
                </div>
                <div className="text-white text-sm mt-1">{dragonHealth}/100</div>
              </div>
              
              <div 
                className={`text-9xl transition-all duration-300 ${
                  dragonAttacking ? 'scale-125 animate-pulse' : 'scale-100'
                } ${isAttacking ? 'opacity-50 shake' : 'opacity-100'}`}
              >
                🐉
              </div>
              
              {question && (
                <div className="mt-4 bg-white/20 backdrop-blur-sm rounded-lg p-4 text-white">
                  <div className="text-3xl font-bold">
                    {question.a} {question.op} {question.b} = ?
                  </div>
                </div>
              )}
            </div>

            {/* Player Side */}
            <div className="text-center">
              <div className="mb-4">
                <div className="text-sm text-white mb-2">Your HP</div>
                <div className="w-full bg-gray-700 rounded-full h-4 overflow-hidden">
                  <div 
                    className="bg-gradient-to-r from-green-500 to-blue-500 h-full transition-all duration-300"
                    style={{ width: `${playerHealth}%` }}
                  />
                </div>
                <div className="text-white text-sm mt-1">{playerHealth}/100</div>
              </div>
              
              <div 
                className={`text-9xl transition-all duration-300 ${
                  isAttacking ? 'scale-125' : 'scale-100'
                } ${dragonAttacking ? 'opacity-50 shake' : 'opacity-100'}`}
              >
                🧙‍♂️
              </div>

              <form onSubmit={handleSubmit} className="mt-4">
                <input
                  type="number"
                  step="0.1"
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  className="w-full text-3xl text-center border-4 border-yellow-400 rounded-lg p-3 mb-3 focus:border-yellow-500 focus:outline-none bg-white/90"
                  placeholder="?"
                  autoFocus
                />
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white text-xl font-bold py-3 rounded-lg hover:from-yellow-600 hover:to-orange-600 transition-all shadow-lg"
                >
                  ⚔️ Attack!
                </button>
              </form>
            </div>
          </div>

          {/* Feedback */}
          {feedback && (
            <div className={`text-center mt-4 text-2xl font-bold ${
              feedback.includes('💥') || feedback.includes('🎉') ? 'text-green-400' : 'text-red-400'
            }`}>
              {feedback}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 text-white text-sm">
          <p><strong>⚔️ Battle Instructions:</strong></p>
          <p>• Solve decimal problems to attack the dragon</p>
          <p>• Each correct answer deals 25 damage</p>
          <p>• Dragon attacks every 5 seconds (-10 HP)</p>
          <p>• Wrong answers cost you 5 HP</p>
          <p>• Defeat dragons to level up and earn points!</p>
        </div>
      </div>

      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-10px); }
          75% { transform: translateX(10px); }
        }
        .shake {
          animation: shake 0.3s;
        }
      `}</style>
    </div>
  );
};

export default DecimalDragons;